import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';

const DemoModeContext = createContext({
  active: false,
  hasRealCalendar: false,
  calendarTabVisible: false,
  loading: false,
  refresh: async () => {},
  enter: async () => {},
  exit: async () => {},
});

export const DemoModeProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [state, setState] = useState({
    active: false,
    hasRealCalendar: false,
    calendarTabVisible: false,
    demoChannelIds: [],
    demoConversationIds: [],
    loading: false,
    initialized: false,
  });

  const refresh = useCallback(async () => {
    if (!token || !user?.organization_id) {
      setState((s) => ({ ...s, active: false, hasRealCalendar: false, calendarTabVisible: false, initialized: true }));
      return;
    }
    try {
      const res = await axios.get(`${API}/demo-mode/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        active: !!res.data.active,
        hasRealCalendar: !!res.data.has_real_calendar,
        calendarTabVisible: !!res.data.calendar_tab_visible,
        demoChannelIds: res.data.demo_channel_ids || [],
        demoConversationIds: res.data.demo_conversation_ids || [],
        loading: false,
        initialized: true,
      });
    } catch (e) {
      // mark initialized on error so the guard can fall through
      setState((s) => ({ ...s, initialized: true }));
    }
  }, [token, user?.organization_id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enter = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.post(`${API}/demo-mode/enter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        active: !!res.data.active,
        hasRealCalendar: !!res.data.has_real_calendar,
        calendarTabVisible: !!res.data.calendar_tab_visible,
        demoChannelIds: res.data.demo_channel_ids || [],
        demoConversationIds: res.data.demo_conversation_ids || [],
        loading: false,
      });
      return res.data;
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      throw e;
    }
  };

  const exit = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await axios.post(`${API}/demo-mode/exit`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState({
        active: !!res.data.active,
        hasRealCalendar: !!res.data.has_real_calendar,
        calendarTabVisible: !!res.data.calendar_tab_visible,
        demoChannelIds: res.data.demo_channel_ids || [],
        demoConversationIds: res.data.demo_conversation_ids || [],
        loading: false,
      });
      return res.data;
    } catch (e) {
      setState((s) => ({ ...s, loading: false }));
      throw e;
    }
  };

  return (
    <DemoModeContext.Provider
      value={{
        active: state.active,
        hasRealCalendar: state.hasRealCalendar,
        calendarTabVisible: state.calendarTabVisible,
        demoChannelIds: state.demoChannelIds,
        demoConversationIds: state.demoConversationIds,
        loading: state.loading,
        initialized: state.initialized,
        refresh,
        enter,
        exit,
      }}
    >
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoMode = () => useContext(DemoModeContext);

export default DemoModeContext;
